import { ApiError, api, nullOn404, on404 } from 'shared/api/client';
import { apiErrorText } from 'shared/api/error-message';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { keepPreviousData, QueryClient, useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  Tag,
  CreateTagInput,
  CreateBlockChainInput,
  ContinueTagInput,
  UpdateTagInput,
  DeleteTagInput,
  ExistsResponse,
  SuccessResponse,
  UUID,
  TagsByDocumentOptions,
  TagsByDocumentResponse,
  PaginatedTagsParams,
  PaginatedTagsResponse,
} from './types';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n, i18nDynamic } from 'shared/i18n';
import {
  invalidateOnTagCreate,
  invalidateOnBatchCreate,
  invalidateOnBlockChain,
  invalidateOnContinue,
  invalidateOnTagUpdate,
  invalidateOnTagDelete,
} from './tagCacheInvalidation';

import { tagQueryKeys } from './keys';

export function useTag(tagId: UUID | undefined): UseQueryResult<Tag | null> {
  return useQuery({
    queryKey: [tagQueryKeys.tag, tagId],

    queryFn: () =>
      api
        .GET('/tags/{id}', { params: { path: { id: tagId as string } } })
        .then((res) => res.data)
        .catch(nullOn404),

    enabled: !!tagId,
  });
}

export function useTagExists(tagId: UUID | undefined): UseQueryResult<ExistsResponse> {
  return useQuery({
    queryKey: [tagQueryKeys.tagExists, tagId],

    queryFn: () =>
      api
        .GET('/tags/{id}/exists', { params: { path: { id: tagId as string } } })
        .then((res) => res.data)
        .catch(on404({ exists: false })),

    enabled: !!tagId,
  });
}

export function useTagsByDocument(documentSuperId: number | undefined, options?: TagsByDocumentOptions): UseQueryResult<TagsByDocumentResponse> {
  const includeList = options?.include?.length ? [...options.include].sort() : undefined;
  const include = includeList?.join(',');

  return useQuery({
    queryKey: [tagQueryKeys.tagsByDocument, documentSuperId, includeList],

    queryFn: () =>
      api
        .GET('/tags/document/{documentSuperId}', {
          params: { path: { documentSuperId: String(documentSuperId) }, query: include ? { include } : {} },
        })
        .then((res) => res.data)
        .catch(on404<TagsByDocumentResponse>({ tags: [], taxonomy: {} })),

    enabled: !!documentSuperId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export function useTagsByElement(elementId: UUID | undefined): UseQueryResult<Tag[]> {
  return useQuery({
    queryKey: [tagQueryKeys.tagsByElement, elementId],

    queryFn: () =>
      api
        .GET('/tags/element/{elementId}', { params: { path: { elementId: elementId as string } } })
        .then((res) => res.data)
        .catch(on404([])),

    enabled: !!elementId,
  });
}

export function useTagsByDocumentPaginated(documentSuperId: number | undefined, params: PaginatedTagsParams): UseQueryResult<PaginatedTagsResponse> {
  const queryParams = new URLSearchParams();
  if (params.page != null) queryParams.set('page', String(params.page));
  if (params.pageSize != null) queryParams.set('pageSize', String(params.pageSize));
  if (params.search) queryParams.set('search', params.search);
  if (params.status?.length) queryParams.set('status', params.status.join(','));
  if (params.validationStatus?.length) queryParams.set('validationStatus', params.validationStatus.join(','));
  if (params.type?.length) queryParams.set('type', params.type.join(','));
  if (params.taxonomy?.length) queryParams.set('taxonomy', params.taxonomy.join(','));
  if (params.currency?.length) queryParams.set('currency', params.currency.join(','));
  if (params.contextId?.length) queryParams.set('contextId', params.contextId.join(','));
  if (params.include?.length) queryParams.set('include', params.include.join(','));

  const query = Object.fromEntries(queryParams) as Record<string, string>;

  return useQuery({
    queryKey: [tagQueryKeys.tagsByDocumentPaginated, documentSuperId, params],

    queryFn: () =>
      api
        .GET('/tags/document/{documentSuperId}/paginated', {
          params: { path: { documentSuperId: String(documentSuperId) }, query },
        })
        .then((res) => res.data)
        .catch(
          on404<PaginatedTagsResponse>({
            rows: [],
            count: 0,
            stats: { total: 0, approved: 0, draft: 0, needsAttention: 0, invalid: 0, mandatory: 0 },
            totalMandatoryTags: 0,
            chains: {},
          })
        ),

    enabled: !!documentSuperId,
    placeholderData: keepPreviousData,
  });
}

export const createTagErrorMessage = (error: unknown): string => {
  const key = error instanceof ApiError ? error.message : undefined;
  if (key?.startsWith('tag.')) return i18nDynamic(key);
  return apiErrorText(error, i18n('tag:created.error'));
};

export function useCreateTag(): UseMutationResult<Tag, unknown, CreateTagInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tag: CreateTagInput) => {
      const res = await api.POST('/tags/', { body: tag });
      return res?.data;
    },

    onError: (error: unknown) => {
      const errorMessage = createTagErrorMessage(error);
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: errorMessage,
        removeTime: 4000,
      });
    },

    onSuccess: (_data, variables) => {
      if (variables.origin !== 'ai') {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag:created.success'),
          removeTime: 4000,
        });
      }
      invalidateOnTagCreate(queryClient, variables);
    },
  });
}

export function useBatchCreateTags(): UseMutationResult<Tag[], unknown, CreateTagInput[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tags: CreateTagInput[]) => {
      const res = await api.POST('/tags/batch', { body: { tags } });
      return res?.data;
    },

    onError: (error: unknown) => {
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: createTagErrorMessage(error), removeTime: 4000 });
    },

    onSuccess: (_data, tags) => {
      invalidateOnBatchCreate(queryClient, tags[0]?.documentSuperId);
    },
  });
}

export function useCreateBlockChain(): UseMutationResult<Tag[], unknown, CreateBlockChainInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBlockChainInput) => {
      const res = await api.POST('/tags/block-chain', { body: input });
      return res?.data;
    },

    onError: (error: unknown) => {
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: createTagErrorMessage(error), removeTime: 4000 });
    },

    onSuccess: (_data, input) => {
      invalidateOnBlockChain(queryClient, input.documentSuperId);
    },
  });
}

export function useContinueTag({ silent = false }: { silent?: boolean } = {}): UseMutationResult<Tag, unknown, ContinueTagInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tag: ContinueTagInput) => {
      const res = await api.POST('/tags/continue', { body: tag });
      return res?.data;
    },

    onError: (error: unknown) => {
      if (silent) return;
      const errorMessage = createTagErrorMessage(error);
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: errorMessage,
        removeTime: 4000,
      });
    },

    onSuccess: (_data, variables) => {
      if (!silent && variables.origin !== 'ai') {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('tag:created.success'),
          removeTime: 4000,
        });
      }
      invalidateOnContinue(queryClient, variables);
    },
  });
}

export function useUpdateTag(): UseMutationResult<SuccessResponse, unknown, UpdateTagInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...tagData }: UpdateTagInput) => {
      const res = await api.PUT('/tags/{id}', { params: { path: { id } }, body: tagData });
      return res?.data;
    },

    onError: (error: unknown) => {
      const errorMessage = apiErrorText(error, i18n('tag:updated.error'));
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: errorMessage,
        removeTime: 4000,
      });
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:updated.success'),
        removeTime: 4000,
      });
      invalidateOnTagUpdate(queryClient, variables);
    },
  });
}

export function useDeleteTag(): UseMutationResult<SuccessResponse, unknown, DeleteTagInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, skipSectionRewrite }: DeleteTagInput) => {
      const res = await api.DELETE('/tags/{id}', { params: { path: { id }, query: skipSectionRewrite ? { skipSectionRewrite: 'true' } : {} } });
      return res?.data;
    },

    onError: (error) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('tag:deleted.error'),
        removeTime: 4000,
      });
      ServerErrorHandler(error);
    },

    onSuccess: (_data, variables) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('tag:deleted.success'),
        removeTime: 4000,
      });
      invalidateOnTagDelete(queryClient, variables);
    },
  });
}

export type BulkDeleteTagsInput = { ids: UUID[]; documentSuperId: number; skipSectionRewrite?: boolean };

const postBulkDelete = async ({ ids, documentSuperId, skipSectionRewrite }: BulkDeleteTagsInput) => {
  const res = await api.POST('/tags/document/{documentSuperId}/bulk-delete', {
    params: { path: { documentSuperId: String(documentSuperId) } },
    body: { ids, skipSectionRewrite },
  });
  return res?.data;
};

const invalidateOnBulkDelete = (queryClient: QueryClient, { ids, documentSuperId }: BulkDeleteTagsInput) => {
  if (ids.length === 0) return;
  queryClient.invalidateQueries({ queryKey: [tagQueryKeys.tag] });
  invalidateOnTagDelete(queryClient, { id: ids[0], documentSuperId });
};

export type DiscardTagsInput = { ids: UUID[]; documentSuperId: number };

export function useDiscardTags(): UseMutationResult<{ deletedCount: number } | undefined, unknown, DiscardTagsInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DiscardTagsInput) => postBulkDelete({ ...input, skipSectionRewrite: true }),
    onSettled: (_data, _error, input) => invalidateOnBulkDelete(queryClient, input),
  });
}

export function useBulkDeleteTags(): UseMutationResult<{ deletedCount: number } | undefined, unknown, BulkDeleteTagsInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postBulkDelete,

    onError: (error, { ids }) => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: apiErrorText(error, i18n('tag:manage.deleted.error', { count: ids.length })),
        removeTime: 4000,
      });
    },

    onSuccess: (data, { ids }) => {
      const count = data?.deletedCount ?? ids.length;
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'success', text: i18n('tag:manage.deleted.success', { count }), removeTime: 4000 });
    },

    onSettled: (_data, _error, input) => invalidateOnBulkDelete(queryClient, input),
  });
}
